import React, { useState, useEffect } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { useTutor } from '../../contexts/TutorContext';
import { MaterialIcons } from '@expo/vector-icons';

interface AvatarContainerProps {
  duration: number;
}

export const AvatarContainer: React.FC<AvatarContainerProps> = ({ duration }) => {
  const { selectedTutor } = useTutor();
  const [formattedDuration, setFormattedDuration] = useState('00:00');

  useEffect(() => {
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    setFormattedDuration(
      `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    );
  }, [duration]);

  const styles = StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 10,
      paddingRight: 10,
      width: '100%',
      maxWidth: 800,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    avatarWrapper: {
      position: 'relative',
      width: 120,
      height: 120,
      borderRadius: 60,
      overflow: 'visible',
      backgroundColor: '#d0d0d0',
      '@media (max-width: 768px)': {
        width: 100,
        height: 100,
        borderRadius: 50,
      },
    },
    avatar: {
      width: '100%',
      height: '100%',
      resizeMode: 'cover',
      borderRadius: '50%',
    },
    placeholderAvatar: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#e0e0e0',
    },
    tutorName: {
      fontSize: 24,
      fontWeight: '200',
      color: '#d0d0d0',
      marginTop: 2,
      marginBottom: 5,
      '@media (max-width: 768px)': {
        fontSize: 20,
      },
    },
    infoContainer: {
      position: 'absolute',
      bottom: 2,
      left: '103%',
      transform: 'translateX(-50%) scale(1.1)',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      zIndex: 2,
    },
    duration: {
      fontSize: 12,
      color: '#d0d0d0',
      fontFamily: 'monospace',
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    },
    flagWrapper: {
      width: 32,
      height: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#fff',
      borderRadius: 4,
      padding: 0,
      borderWidth: 1,
      borderColor: '#fff',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    },
    flag: {
      width: '100%',
      height: '100%',
      borderRadius: 3,
      objectFit: 'cover',
    },
  });

  return (
    <View style={styles.container}>
      <View style={styles.avatarWrapper}>
        {selectedTutor?.avatar_url ? (
          <Image
            source={{ uri: selectedTutor.avatar_url }}
            style={styles.avatar}
          />
        ) : (
          <View style={styles.placeholderAvatar}>
            <MaterialIcons name="person" size={60} color="#999" />
          </View>
        )}
        <View style={styles.infoContainer}>
          {selectedTutor?.country_code && (
            <View style={styles.flagWrapper}>
              <Image
                source={{
                  uri: `https://flagcdn.com/h24/${selectedTutor.country_code.toLowerCase()}.png`,
                }}
                style={styles.flag}
              />
            </View>
          )}
          <Text style={styles.duration}>{formattedDuration}</Text>
        </View>
      </View>
      <Text style={styles.tutorName}>{selectedTutor?.name || 'Loading...'}</Text>
    </View>
  );
};
